import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Plus,
  Search,
  Bot,
  Pencil,
  Trash2,
  Eye,
  Power,
  PowerOff,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import AdminLayout from "@/components/layout/AdminLayout";
import { useToast } from "@/hooks/use-toast";
import {
  fetchAiPersonas,
  toggleAiPersonaActive,
  deleteAiPersona,
} from "@/api/aiPersonas";
import { AiPersona } from "@/types/aiPersona";

const AiPersonasList = () => {
  const [personas, setPersonas] = useState<AiPersona[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [confirmAction, setConfirmAction] = useState<{
    type: "toggle" | "delete";
    persona: AiPersona;
  } | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    loadPersonas();
  }, []);

  const loadPersonas = async () => {
    try {
      setLoading(true);
      const data = await fetchAiPersonas();
      setPersonas(data);
    } catch (error) {
      console.error("Error fetching personas:", error);
      toast({
        title: "Error",
        description: "Failed to load AI personas.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (persona: AiPersona) => {
    try {
      await toggleAiPersonaActive(persona.id, !persona.isActive);
      toast({
        title: persona.isActive ? "Deactivated" : "Activated",
        description: `"${persona.name}" is now ${persona.isActive ? "inactive" : "active"}.`,
      });
      await loadPersonas();
    } catch (error) {
      console.error("Error toggling persona:", error);
      toast({
        title: "Error",
        description: "Failed to update persona status.",
        variant: "destructive",
      });
    } finally {
      setConfirmAction(null);
    }
  };

  const handleDelete = async (persona: AiPersona) => {
    try {
      await deleteAiPersona(persona.id);
      toast({
        title: "Deleted",
        description: `"${persona.name}" has been deleted.`,
      });
      await loadPersonas();
    } catch (error) {
      console.error("Error deleting persona:", error);
      toast({
        title: "Error",
        description:
          error instanceof Error
            ? error.message
            : "Failed to delete persona.",
        variant: "destructive",
      });
    } finally {
      setConfirmAction(null);
    }
  };

  const filteredPersonas = personas.filter((p) => {
    const matchesSearch =
      p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.slug.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus =
      statusFilter === "all" ||
      (statusFilter === "active" && p.isActive) ||
      (statusFilter === "inactive" && !p.isActive);
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <AdminLayout title="AI Personas">
        <div className="flex h-64 w-full items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="AI Personas">
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <p className="text-muted-foreground">
            Manage AI personas that define assistant behavior and personality.
          </p>
          <Button asChild>
            <Link to="/admin/ai/personas/new">
              <Plus className="h-4 w-4 mr-2" />
              New Persona
            </Link>
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bot className="h-5 w-5" />
              Personas ({filteredPersonas.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                <Input
                  placeholder="Search personas..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-input bg-background rounded-md text-sm"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Slug</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Templates</TableHead>
                  <TableHead>Updated</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPersonas.map((persona) => (
                  <TableRow key={persona.id}>
                    <TableCell className="font-medium">
                      {persona.name}
                    </TableCell>
                    <TableCell>
                      <code className="text-xs bg-muted px-1.5 py-0.5 rounded">
                        {persona.slug}
                      </code>
                    </TableCell>
                    <TableCell className="max-w-[200px]">
                      <span className="line-clamp-1 text-sm text-muted-foreground">
                        {persona.description || "—"}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={persona.isActive ? "default" : "secondary"}
                      >
                        {persona.isActive ? "Active" : "Inactive"}
                      </Badge>
                    </TableCell>
                    <TableCell>{persona.templateCount ?? 0}</TableCell>
                    <TableCell className="text-sm">
                      {new Date(persona.updatedAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="outline" size="sm" asChild>
                          <Link to={`/admin/ai/personas/${persona.id}`}>
                            <Eye className="h-3 w-3 mr-1" />
                            View
                          </Link>
                        </Button>
                        <Button variant="outline" size="sm" asChild>
                          <Link to={`/admin/ai/personas/${persona.id}/edit`}>
                            <Pencil className="h-3 w-3 mr-1" />
                            Edit
                          </Link>
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            setConfirmAction({ type: "toggle", persona })
                          }
                        >
                          {persona.isActive ? (
                            <PowerOff className="h-3 w-3 mr-1" />
                          ) : (
                            <Power className="h-3 w-3 mr-1" />
                          )}
                          {persona.isActive ? "Deactivate" : "Activate"}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() =>
                            setConfirmAction({ type: "delete", persona })
                          }
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {filteredPersonas.length === 0 && (
              <div className="text-center py-8">
                <Bot className="mx-auto h-12 w-12 text-muted-foreground" />
                <h3 className="mt-2 text-sm font-semibold">
                  No personas found
                </h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  {searchTerm || statusFilter !== "all"
                    ? "Try adjusting your search criteria."
                    : "Get started by creating your first AI persona."}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Confirmation Dialog */}
      <AlertDialog
        open={!!confirmAction}
        onOpenChange={() => setConfirmAction(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmAction?.type === "toggle"
                ? confirmAction.persona.isActive
                  ? "Deactivate Persona?"
                  : "Activate Persona?"
                : "Delete Persona?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmAction?.type === "toggle"
                ? confirmAction.persona.isActive
                  ? `This will deactivate "${confirmAction.persona.name}". Frontend apps will no longer see this persona.`
                  : `This will activate "${confirmAction.persona.name}". Frontend apps will be able to use this persona.`
                : `This will permanently delete "${confirmAction?.persona.name}". This action cannot be undone.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (!confirmAction) return;
                if (confirmAction.type === "toggle") {
                  handleToggle(confirmAction.persona);
                } else {
                  handleDelete(confirmAction.persona);
                }
              }}
              className={
                confirmAction?.type === "delete"
                  ? "bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  : ""
              }
            >
              {confirmAction?.type === "toggle"
                ? confirmAction?.persona.isActive
                  ? "Deactivate"
                  : "Activate"
                : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
};

export default AiPersonasList;
